import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  INSTAGRAM_REEL_FAVOURITE_TYPE,
  NEWS_FAVOURITE_TYPE,
  PODCAST_FAVOURITE_TYPE,
  WEBINAR_FAVOURITE_TYPE,
} from '@domain/features/user-favourites/user-favourites-feature'
import type { UserFavouriteType } from '@domain/models/user-favourite'
import FavouriteBookmarkButton from '@presentation/components/ui/FavouriteBookmarkButton'
import InstagramReelCard from '@presentation/components/ui/InstagramReelCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import NewsCard from '@presentation/components/ui/NewsCard'
import PodcastCard from '@presentation/components/ui/PodcastCard'
import WebinarCard from '@presentation/components/ui/WebinarCard'
import { useNewsCategories } from '@presentation/hooks/useNewsCategories'
import { useUserFavourites } from '@presentation/hooks/useUserFavourites'
import SeoHead from '@presentation/seo/SeoHead'

const FILTER_OPTIONS: { type: UserFavouriteType, label: string }[] = [
  { type: NEWS_FAVOURITE_TYPE, label: 'News' },
  { type: WEBINAR_FAVOURITE_TYPE, label: 'Webinars' },
  { type: PODCAST_FAVOURITE_TYPE, label: 'Prof Bas Bloem Weekly' },
  { type: INSTAGRAM_REEL_FAVOURITE_TYPE, label: 'Prof Michael Okun: My Take' },
]

export default function UserFavoritesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { categories } = useNewsCategories()
  const [selectedType, setSelectedType] = useState<UserFavouriteType>(NEWS_FAVOURITE_TYPE)
  const {
    itemsByType,
    loading,
    error,
    isAuthenticated,
    toggleFavourite,
    isToggling,
  } = useUserFavourites()

  const categoryMap = useMemo(
    () => new Map(categories.map((category) => [category.uid, category.name])),
    [categories],
  )

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const selectedItems = useMemo(() => {
    if (selectedType === NEWS_FAVOURITE_TYPE) {
      return itemsByType.news
    }
    if (selectedType === PODCAST_FAVOURITE_TYPE) {
      return itemsByType.podcasts
    }
    if (selectedType === WEBINAR_FAVOURITE_TYPE) {
      return itemsByType.webinars
    }
    return itemsByType.instagramReels
  }, [itemsByType, selectedType])

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      <SeoHead
        title="Your Favourites | Parkinson's Insights"
        description="View your saved Parkinson's Insights content by section."
        canonicalPath="/favorites"
        noindex
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-5 ml-1 flex items-center justify-between gap-3 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
              Your Favourites
            </h1>
            <p className="mt-2 text-sm font-semibold text-text-secondary sm:text-base">
              Here are your favourites, categorised by type.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2 rounded-xl border border-border-light bg-surface p-1 shadow-sm sm:inline-flex sm:max-w-full sm:flex-row sm:flex-wrap">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.type}
              type="button"
              onClick={() => {
                setSelectedType(option.type)
              }}
              className={`w-full rounded-lg px-4 py-2 text-left text-sm font-semibold transition-colors sm:w-auto sm:text-center ${
                selectedType === option.type
                  ? 'bg-blue text-white'
                  : 'text-text-secondary hover:bg-border-light'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner className="mt-8" />
        ) : !isAuthenticated ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              Log in to see your saved items.
            </p>
            <button
              type="button"
              onClick={openLoginModal}
              className="mt-4 rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-light"
            >
              Log In
            </button>
          </div>
        ) : error ? (
          <p className="mt-8 text-center text-text-secondary">
            {error}
          </p>
        ) : selectedItems.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              You haven&apos;t saved any items in this section yet.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {selectedItems.map((item) => {
              if (item.type === NEWS_FAVOURITE_TYPE) {
                return (
                  <div key={item.favourite.uid} className="relative">
                    <NewsCard
                      activity={item.activity}
                      variant="compact"
                      categoryName={
                        item.activity.newsCategoryId
                          ? categoryMap.get(item.activity.newsCategoryId)
                          : undefined
                      }
                    />
                    <FavouriteBookmarkButton
                      isFavourited
                      onClick={() => {
                        void toggleFavourite(item.activity.uid, NEWS_FAVOURITE_TYPE, openLoginModal)
                      }}
                      disabled={isToggling(item.activity.uid, NEWS_FAVOURITE_TYPE)}
                      className="absolute right-3 top-3 z-10 h-10 w-10 shadow-sm"
                      ariaLabel="Remove article from favourites"
                    />
                  </div>
                )
              }

              if (item.type === PODCAST_FAVOURITE_TYPE) {
                return (
                  <div key={item.favourite.uid} className="relative">
                    <PodcastCard episode={item.episode} />
                    <FavouriteBookmarkButton
                      isFavourited
                      onClick={() => {
                        void toggleFavourite(item.episode.uid, PODCAST_FAVOURITE_TYPE, openLoginModal)
                      }}
                      disabled={isToggling(item.episode.uid, PODCAST_FAVOURITE_TYPE)}
                      className="absolute right-3 top-3 z-10 h-10 w-10 shadow-sm"
                      ariaLabel="Remove podcast from favourites"
                    />
                  </div>
                )
              }

              if (item.type === WEBINAR_FAVOURITE_TYPE) {
                return (
                  <div key={item.favourite.uid} className="relative">
                    <WebinarCard webinar={item.webinar} />
                    <FavouriteBookmarkButton
                      isFavourited
                      onClick={() => {
                        void toggleFavourite(item.webinar.uid, WEBINAR_FAVOURITE_TYPE, openLoginModal)
                      }}
                      disabled={isToggling(item.webinar.uid, WEBINAR_FAVOURITE_TYPE)}
                      className="absolute right-3 top-3 z-10 h-10 w-10 shadow-sm"
                      ariaLabel="Remove webinar from favourites"
                    />
                  </div>
                )
              }

              return (
                <div key={item.favourite.uid} className="relative">
                  <InstagramReelCard reel={item.reel} />
                  <FavouriteBookmarkButton
                    isFavourited
                    onClick={() => {
                      void toggleFavourite(item.reel.uid, INSTAGRAM_REEL_FAVOURITE_TYPE, openLoginModal)
                    }}
                    disabled={isToggling(item.reel.uid, INSTAGRAM_REEL_FAVOURITE_TYPE)}
                    className="absolute right-3 top-3 z-10 h-10 w-10 shadow-sm"
                    ariaLabel="Remove Instagram content from favourites"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
